// Settings Store - Configuracion del agente (persistida en localStorage)
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

const DEFAULTS = {
  agentUrl: 'http://localhost:5000',
  agentApiKey: '',
  agentTimeout: 30000,
} as const;

interface SettingsState {
  agentUrl: string;
  agentApiKey: string;
  agentTimeout: number;
}

interface SettingsActions {
  setAgentUrl: (url: string) => void;
  setAgentApiKey: (key: string) => void;
  setAgentTimeout: (timeout: number) => void;
  resetToDefaults: () => void;
}

type SettingsStore = SettingsState & SettingsActions;

/**
 * Lee el estado actual del store (in-memory, siempre sincronico).
 * Funciona fuera de React gracias a useSettingsStore.getState().
 * Fallback a localStorage para el caso edge donde el store aun no se hidrató.
 */
function _getCurrentSettings(): SettingsState {
  // El store podria no estar hidratado al primer import del modulo,
  // asi que intentamos primero el store y luego localStorage como fallback.
  try {
    const storeState = useSettingsStore.getState();
    // Si el store ya tiene datos (no son los iniciales vacios), usarlos
    if (storeState.agentApiKey || storeState.agentUrl || storeState.agentTimeout) {
      return storeState;
    }
  } catch {
    // Store no inicializado aun
  }

  // Fallback: leer directo de localStorage
  try {
    const raw = localStorage.getItem('agent-settings');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.state) return parsed.state as SettingsState;
    }
  } catch {
    // localStorage no disponible o datos corruptos
  }

  return { agentUrl: '', agentApiKey: '', agentTimeout: 0 };
}

/**
 * Resuelve el valor efectivo con prioridad: store/localStorage > env var > default.
 * Se usa desde agentClient.ts (fuera de React) para no depender del hook.
 */
export function getEffectiveAgentUrl(): string {
  const stored = _getCurrentSettings();
  return stored.agentUrl || import.meta.env.VITE_AGENT_URL || DEFAULTS.agentUrl;
}

export function getEffectiveApiKey(): string {
  const stored = _getCurrentSettings();
  return stored.agentApiKey || import.meta.env.VITE_RPA_AGENT_API_KEY || DEFAULTS.agentApiKey;
}

export function getEffectiveTimeout(): number {
  const stored = _getCurrentSettings();
  if (stored.agentTimeout) return stored.agentTimeout;
  const envTimeout = Number(import.meta.env.VITE_AGENT_TIMEOUT);
  return envTimeout > 0 ? envTimeout : DEFAULTS.agentTimeout;
}

export const SETTING_DEFAULTS = DEFAULTS;

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set) => ({
      agentUrl: '',
      agentApiKey: '',
      agentTimeout: 0,

      setAgentUrl: (url: string) => set({ agentUrl: url }),
      setAgentApiKey: (key: string) => set({ agentApiKey: key }),
      setAgentTimeout: (timeout: number) => set({ agentTimeout: timeout }),
      resetToDefaults: () => set({ agentUrl: '', agentApiKey: '', agentTimeout: 0 }),
    }),
    {
      name: 'agent-settings',
      // H-T005: NOTA: La API key se almacena sin cifrar en localStorage.
      // Aceptable para agente local (localhost), pero el usuario debe ser consciente
      // de que cualquier extensión o script con acceso al localStorage puede leerla.
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        agentUrl: state.agentUrl,
        agentApiKey: state.agentApiKey,
        agentTimeout: state.agentTimeout,
      }),
    }
  )
);
